import { delay } from '@/shared/datetime/utils/time'

import { CAPTURE_WINDOW_SECONDS } from './capture-window'
import { COLLECTOR_FIXTURES } from './fixtures/collectors'
import { buildDiagnosticsChecks } from './fixtures/diagnostics'
import { LOG_SOURCE_FIXTURES } from './fixtures/log-sources'
import { buildCaptureLines } from './fixtures/raw-logs'

import type {
	CaptureSnapshot,
	CollectorWithSources,
	DiagnosticsRun,
	LogSourceWithCollector,
} from './types'

/**
 * How long the fixture "request" takes.
 *
 * Not decoration: it is what makes the skeleton, the Suspense boundary and the
 * route's `pendingComponent` render during development instead of being
 * dead code that first executes in front of a customer. Short enough not to make
 * clicking around the prototype tedious.
 */
const FIXTURE_LATENCY_MS = 350

/**
 * Resolves one fixture Collector's authored offsets against the clock.
 *
 * Shared by both fetchers so the list and the detail page cannot disagree about
 * a Collector — the real endpoints will return the same shape from the same
 * table, and a second hand-rolled mapping here would drift from the first.
 */
function resolveCollector(
	fixture: (typeof COLLECTOR_FIXTURES)[number],
	now: number,
): CollectorWithSources {
	const { lastSeenSecondsAgo, ...collector } = fixture

	return {
		...collector,
		// Resolved against the clock at fetch time, so the authored offsets stay
		// truthful however long ago the fixture was written — and so a refresh
		// visibly moves the "last seen" readout, as the real one will.
		lastSeenAt: new Date(now - lastSeenSecondsAgo * 1000).toISOString(),
		sources: LOG_SOURCE_FIXTURES.filter(
			(source) => source.collectorId === collector.id,
		).map(({ lastLogSecondsAgo, ...source }) => ({
			...source,
			// `null` survives as `null` rather than collapsing to the epoch: a
			// source that has never received a log has no last-log time, and the
			// detail table renders that state instead of an age.
			lastLogAt:
				lastLogSecondsAgo === null
					? null
					: new Date(now - lastLogSecondsAgo * 1000).toISOString(),
		})),
	}
}

/**
 * The Collectors list, with the Log Sources hosted on each.
 *
 * **This is the swap seam.** There is no Collectors endpoint yet; when one
 * lands, this body becomes a `get()` call and the two fixture files are deleted.
 * Nothing above it — query factory, components, skeletons — should have to
 * change, which is the whole reason the fixtures are reached through the query
 * layer rather than imported by a component.
 *
 * `signal` is honoured rather than accepted-and-ignored, so a tenant switch or a
 * fast back-navigation cancels this the way it will cancel the real request
 * (P-03).
 */
export async function fetchCollectors(
	signal?: AbortSignal,
): Promise<CollectorWithSources[]> {
	await delay(FIXTURE_LATENCY_MS)
	signal?.throwIfAborted()

	const now = Date.now()

	return COLLECTOR_FIXTURES.map((fixture) => resolveCollector(fixture, now))
}

/**
 * One Collector by id, with its Log Sources. **The detail page's swap seam.**
 *
 * Returns `null` for an id nothing matches rather than throwing, because the
 * page has a designed not-found state and an id that does not exist is an
 * expected outcome of a pasted or stale link — not an error worth handing to the
 * error boundary. When the endpoint lands this becomes a `get()` whose 404 is
 * mapped to the same `null`.
 */
export async function fetchCollector(
	collectorId: string,
	signal?: AbortSignal,
): Promise<CollectorWithSources | null> {
	await delay(FIXTURE_LATENCY_MS)
	signal?.throwIfAborted()

	const fixture = COLLECTOR_FIXTURES.find(({ id }) => id === collectorId)

	return fixture ? resolveCollector(fixture, Date.now()) : null
}

/**
 * One Log Source, resolved through its Collector.
 *
 * Reached via `resolveCollector` rather than by filtering `LOG_SOURCE_FIXTURES`
 * directly, so the manage page cannot disagree with the table that linked to it,
 * and so the Collector's own name and liveness come from the same read the
 * source does.
 *
 * Returns `null` for an id nothing matches — the same designed not-found state
 * `fetchCollector` describes, and for the same reason. A source id under the
 * *wrong* Collector is `null` too: the pair in the URL is the identity here, and
 * rendering a source that lives somewhere else would make the header's
 * "collector: X" a lie.
 */
export async function fetchLogSource(
	collectorId: string,
	sourceId: string,
	signal?: AbortSignal,
): Promise<LogSourceWithCollector | null> {
	await delay(FIXTURE_LATENCY_MS)
	signal?.throwIfAborted()

	return resolveLogSource(collectorId, sourceId)
}

/**
 * The Log Source behind one `(collectorId, sourceId)` pair, or `null`.
 *
 * Shared by the fetcher and both on-demand runs below, so a capture or a
 * diagnostics run can never be executed against a different source than the one
 * the page is showing.
 */
function resolveLogSource(
	collectorId: string,
	sourceId: string,
): LogSourceWithCollector | null {
	const fixture = COLLECTOR_FIXTURES.find(({ id }) => id === collectorId)
	if (!fixture) return null

	const collector = resolveCollector(fixture, Date.now())
	const source = collector.sources.find(({ id }) => id === sourceId)
	if (!source) return null

	return {
		...source,
		collectorName: collector.name,
		collectorStatus: collector.status,
	}
}

/**
 * How long the fixture's on-demand runs take.
 *
 * The real capture listens for 30 seconds and the real diagnostics can take 60
 * (both stated on screen), so these are **not** the real durations — they are
 * long enough that the running state is a state the user actually sees, and
 * short enough that clicking through the prototype is not a coffee break. The
 * panels state the true durations in their copy; only the wait is compressed.
 */
const CAPTURE_RUN_MS = 2500
const DIAGNOSTICS_RUN_MS = 2000

/**
 * Run one on-demand capture on the source's port. **A swap seam.**
 *
 * Takes no `AbortSignal`: this is an action, not a read (P-03). Cancelling it
 * would leave the panel with neither a result nor an error.
 *
 * Throws when the pair does not resolve. Unlike the page's own fetch, a miss
 * here is not a designed state — the button only exists on a page that already
 * resolved a source, so a miss means the world changed underneath it, and the
 * panel's error state is the honest answer.
 */
export async function runCapture(
	collectorId: string,
	sourceId: string,
): Promise<CaptureSnapshot> {
	await delay(CAPTURE_RUN_MS)

	const source = resolveLogSource(collectorId, sourceId)
	if (!source) throw new Error('Log source no longer exists')

	const capturedAt = new Date()

	return {
		capturedAt: capturedAt.toISOString(),
		windowSeconds: CAPTURE_WINDOW_SECONDS,
		lines: buildCaptureLines(source, capturedAt),
	}
}

/**
 * Run the collector's diagnostics against one source. **A swap seam.**
 *
 * `deviceIp` is whatever the operator typed into the optional field, empty when
 * they left it blank — the reachability check is what decides what an empty one
 * means (see `buildDiagnosticsChecks`). No `AbortSignal`, for the reason
 * `runCapture` gives.
 */
export async function runDiagnostics(
	collectorId: string,
	sourceId: string,
	deviceIp: string,
): Promise<DiagnosticsRun> {
	await delay(DIAGNOSTICS_RUN_MS)

	const source = resolveLogSource(collectorId, sourceId)
	if (!source) throw new Error('Log source no longer exists')

	return {
		ranAt: new Date().toISOString(),
		checks: buildDiagnosticsChecks(source, deviceIp),
	}
}
